import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { useFetchAdminAreaHierarchy } from "@/api/adminArea/userAdminAreaAPI";
import { fetchDatasetStream } from "@/api/dataset/fetchDatasetStream";
import { useMutateGetWidgetDefaultConfig } from "@/api/widget/useWidgetAPI";
import { AIIcon } from "@/assets/icons";
import { useDataset } from "@/hooks/map/useDataset";
import {
  buildDatasetFilterFromPayload,
  enrichAdminAreaFilters
} from "@/hooks/project/utils";
import useLocalStorage from "@/hooks/store/useLocalStorage";
import useLoadingAndToast from "@/hooks/ui/useLoadingAndToast";
import type { RootState } from "@/store";
import type { DatasetFilterPayload, H3Configuration } from "@/types/dataset";

import {
  LokasiAIContent,
  LokasiAIForm,
  LokasiAIInput,
  LokasiAIModal,
  useLokasiAI
} from "@lokasi-ai-web-sdk/app";
import { Button } from "equator-design-system";

import NotificationContent from "../notification/notificationContent";

interface PendingDataset {
  id: string;
  filters: DatasetFilterPayload[];
  grid_config: H3Configuration | null;
}

const AIConversationManager = ({ children }: { children: React.ReactNode }) => {
  const { conversationId, loadConversation } = useLokasiAI();
  const [savedConversations, setSavedConversations] = useLocalStorage<
    Record<string, string>
  >("lokasi_ai_conversations", {});

  useEffect(() => {
    if (savedConversations["dataset-recommendation"]) {
      loadConversation(savedConversations["dataset-recommendation"], 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      conversationId &&
      savedConversations["dataset-recommendation"] !== conversationId
    ) {
      setSavedConversations({
        ...savedConversations,
        ["dataset-recommendation"]: conversationId
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, savedConversations]);

  return children;
};

export default function DatasetRecommendationAI({
  onClickDisabled
}: Readonly<{
  onClickDisabled?: () => void;
}>) {
  const datasetCount = useSelector(
    (state: RootState) => Object.keys(state.dataset.datasets).length
  );
  const datasetCountRef = useRef(datasetCount);
  const abortControllerRef = useRef<AbortController | null>(null);

  const { showLoading, showError, clearLoadingState } = useLoadingAndToast();

  const { pendingActionData, reportActionResult } = useLokasiAI();
  const { handleAddDataset, generateDatasetData } = useDataset();
  const fetchHierarchy = useFetchAdminAreaHierarchy();

  const [showAIModal, setShowAIModal] = useState(false);
  const [aiModalSize, setAiModalSize] = useState<"full" | "floating">(
    "floating"
  );

  const handleCloseModal = useCallback(() => {
    setShowAIModal(false);
    setAiModalSize("floating");
  }, []);

  const { mutateAsync: fetchWidgetDefaultConfig } =
    useMutateGetWidgetDefaultConfig();

  useEffect(() => {
    if (!pendingActionData) return;

    const { datasets = [] } = pendingActionData.payload as {
      datasets?: PendingDataset[];
    };

    if (datasets.length === 0) return;

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const processDatasets = async () => {
      showLoading(
        <NotificationContent description="Please wait while your datasets are being loaded." />,
        true,
        "dataset-loading"
      );

      const results = await Promise.allSettled(
        datasets.map((dataset, idx) =>
          fetchDatasetStream({
            datasetId: dataset.id,
            filterPayload: dataset.filters,
            h3Configuration: dataset.grid_config,
            signal: controller.signal,
            generateDatasetData
          }).then(async ({ datasetMetadata, rawDataset }) => {
            if (!datasetMetadata || !rawDataset) return;

            const countryId = datasetMetadata.country_id ?? "";
            const hierarchy = countryId ? await fetchHierarchy(countryId) : [];

            let widgetDefaultConfig;
            try {
              widgetDefaultConfig = await fetchWidgetDefaultConfig(dataset.id);
            } catch (error) {
              console.warn("Failed to fetch widget default config", error);
            }

            await handleAddDataset({
              datasetMetadata,
              datasetData: rawDataset,
              savedFilters: enrichAdminAreaFilters(
                buildDatasetFilterFromPayload(
                  dataset.filters,
                  datasetMetadata.schema
                ),
                hierarchy
              ),
              attributesSchema: datasetMetadata.schema,
              h3Configuration: dataset?.grid_config,
              index: datasetCountRef.current + idx,
              widget: widgetDefaultConfig?.data
            });
          })
        )
      );

      const hasError = results.some((r) => r.status === "rejected");

      if (hasError) {
        await reportActionResult("failed");
        showError(
          <NotificationContent description="Failed to load datasets." />
        );
        clearLoadingState();
      } else {
        await reportActionResult("succeeded");
        clearLoadingState();
      }
    };

    processDatasets();

    return () => {
      abortControllerRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingActionData]);

  return (
    <AIConversationManager>
      <Button
        variant="secondary"
        className="btn-secondary flex-shrink  h-8 w-8"
        aria-label="Ask AI Assistant"
        onClick={() => {
          if (onClickDisabled) {
            onClickDisabled();
            return;
          }
          setShowAIModal(true);
        }}
      >
        <AIIcon width={16} height={16} className="fill-primary-teal" />{" "}
      </Button>

      {/* key forces remount on size change to reset internal modal state */}
      <LokasiAIModal
        key={aiModalSize}
        defaultSize={aiModalSize}
        onSizeChange={setAiModalSize}
        isOpen={showAIModal}
        onClose={handleCloseModal}
        position={{
          top: 12,
          right: 102,
          bottom: 12,
          left: "calc(50vw + 32px)"
        }}
        className="z-[1]"
      >
        <LokasiAIForm className="flex flex-col h-full">
          <div className="flex-1 overflow-auto">
            <LokasiAIContent />
          </div>
          <LokasiAIInput placeholder="Ask anything..." />
        </LokasiAIForm>
      </LokasiAIModal>
    </AIConversationManager>
  );
}
